import { Col, Row } from "react-bootstrap";
import Card from "./card.component";
import poweredByStripe from "../../../../../assets/img/powered_by_stripe.svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import BillingService from "../billing.service";
import EmptyCard from "../../list/emptylist.component";
import ConfirmationModal from "../../../shared/confirmation-modal.component";
import LoadingAnimation from "../../lottie-files/loading.lotte";
import { useStripe } from "@stripe/react-stripe-js";

const Cards = () => {
  let poweredByStripeIcon = poweredByStripe;
  const [newCard, setNewCard] = useState(false);
  const [newCardDetails, setNewCardDetails] = useState({
    name: "",
    isDefaultCard: false,
  });

  const stripe = useStripe();

  const getStripeElement = useCallback(() => {
    const element = stripe.elements().create("card", {
      style: {
        base: {
          lineHeight: "30px",
          backgroundColor: "white",
        },
      },
      blockPoBox: true,
      hidePostalCode: true,
    });

    setTimeout(() => {
      element.mount("#stripe-card-element");
    });

    return element;
  }, [stripe]);

  const stripeElement = useMemo(() => {
    if (newCard) {
      return getStripeElement();
    } else {
      return null;
    }
  }, [getStripeElement, newCard]);

  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const confirmationModalRef = useRef();
  const [deletingCardIndex, setDeletingCardIndex] = useState(null);

  const [isInLoadingState, setIsInLoadingState] = useState(false);

  const getCustomerCards = useCallback(async () => {
    try {
      const response = await BillingService.getCards();

      setCards(response?.result?.cards ?? []);
    } catch (error) {
      toast.error(error?.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
      setIsInLoadingState(false);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    getCustomerCards();
  }, [getCustomerCards]);

  function createStripeToken() {
    return stripe.createToken(stripeElement, newCardDetails);
  }

  async function handleAddNewCard() {
    if (!newCardDetails.name) {
      toast.error("Please enter the card holder name.");
      return;
    }

    try {
      setIsInLoadingState(true);
      const result = await createStripeToken();

      if (!result?.error) {
        const response = await BillingService.addNewCard(
          result.token.id,
          newCardDetails.isDefaultCard
        );

        if (response?.status === "Success") {
          setIsLoading(true);
          getCustomerCards();
          toast.success("Card added successfully.");
          setNewCard(false);
        } else {
          if (response?.statusCode === 500) {
            toast.error("Something went wrong, please try again.");
          } else {
            toast.error(
              response?.message ?? "Something went wrong, please try again."
            );
          }
        }
      } else {
        throw new Error(
          result?.error?.message ?? "Failed to create token, please try again."
        );
      }
    } catch (error) {
      toast.error(error?.message ?? "Something went wrong, please try again.");
    } finally {
      setIsInLoadingState(false);
    }
  }

  async function handleSetDefaultCard(cardId, cardIndex) {
    if (cards[cardIndex].default) {
      toast.info(
        "You cannot disable the default card. Atleast one card should be selected as default."
      );
      return;
    }

    const prevDefaultCardIndex = cards.findIndex((c) => c.default);

    if (prevDefaultCardIndex > -1) {
      cards[prevDefaultCardIndex].default = false;
    }

    cards[cardIndex].default = true;
    setCards([...cards]);

    try {
      await BillingService.setDefaultCard(cardId);
      toast.success("Default card updated successfully.");
      getCustomerCards();
    } catch (error) {
      setCards((prevCards) => {
        prevCards[cardIndex].default = !prevCards[cardIndex].default;
        return [...prevCards];
      });
      toast.error(error?.message ?? "Something went wrong");
    }
  }

  async function handleDeleteCard(cardIndex, showModal = false) {
    if (showModal) {
      confirmationModalRef.current.show();
      setDeletingCardIndex(cardIndex);
      return;
    }

    try {
      setIsInLoadingState(true);
      await BillingService.deleteCard(cards[cardIndex].id);

      setCards((prevCards) => {
        prevCards.splice(cardIndex, 1);
        return [...prevCards];
      });
      setDeletingCardIndex(null);
      confirmationModalRef.current.hide();
      getCustomerCards();
      toast.success("Card deleted successfully.");
    } catch (error) {
      toast.error(error?.message ?? "Something went wrong");
      setIsInLoadingState(false);
    }
  }

  return (
    <Row className="mt-4">
      <Col sm={7}>
        {isLoading ? (
          <LoadingAnimation />
        ) : cards.length ? (
          <div className="d-flex flex-column gap-2">
            {cards.map((card, cardIndex) => (
              <Card
                key={card.id}
                card={card}
                onSetDefault={() => handleSetDefaultCard(card.id, cardIndex)}
                onDelete={() => handleDeleteCard(cardIndex, true)}
              />
            ))}
          </div>
        ) : (
          !newCard && (
            <EmptyCard
              messHead={"No Cards Added!"}
              MessDescription={
                "Add your credit card for a seamless payment experience."
              }
              containerStyles={{ boxShadow: "none", border: 0 }}
              buttonText="Add Card"
              onClick={() => setNewCard(true)}
            />
          )
        )}
        {/* Add New Card */}
        {newCard && (
          <div className="cc-card-wrap add-new-card">
            <div className="dark-text poppins-medium">Add New Card</div>
            <div id="stripe-card-element">{/* <CardElement /> */}</div>
            <div>
              <input
                className="form-control mt-3"
                placeholder="Name on Card"
                defaultValue={newCardDetails.name}
                onChange={(event) =>
                  setNewCardDetails({
                    ...newCardDetails,
                    name: event.target.value,
                  })
                }
              />
            </div>
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="d-flex align-items-center">
                <div className="custom-toggle">
                  <input
                    type="checkbox"
                    id="SetNewCardAsDefault"
                    defaultValue={newCardDetails.isDefaultCard}
                    onChange={(event) =>
                      setNewCardDetails({
                        ...newCardDetails,
                        isDefaultCard: Boolean(event.target.value),
                      })
                    }
                  />
                  <label for="SetNewCardAsDefault">Set as Default</label>
                </div>
                <div className="lato-medium font-14 ms-2">Set as Default</div>
              </div>
              <div>
                <button
                  className="btn btn-cancel"
                  onClick={() => setNewCard(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn back-btn ms-2 py-1 poppins-medium"
                  disabled={!stripe || isInLoadingState}
                  onClick={handleAddNewCard}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Add New Card */}
        {!!cards.length && !newCard && (
          <button
            className="btn blue-btn-primary small-btn"
            data-tooltip-id="listtips"
            data-tooltip-content="Add a new Credit Card"
            onClick={() => setNewCard(true)}
          >
            <i className="fa-regular fa-plus me-2"></i>Add Card
          </button>
        )}
      </Col>
      <Col sm={1}></Col>
      <Col sm={4}>
        <div className="secureDate">
          <div className="top">
            <div className="dark-text lato-bold">
              <i className="fa-solid fa-shield-check me-2 green-txt-clr"></i>
              Your Credit Card Data is Secure
            </div>
            <div className="font-12 light-grey-text">
              We never store any cardholder information on our servers. All the
              payments are encrypted and securely processed by Stripe.
            </div>
          </div>
          <div className="bottom">
            <img src={poweredByStripeIcon} alt="" width={120} />
          </div>
        </div>
      </Col>
      <ConfirmationModal
        ref={confirmationModalRef}
        title="Confirm"
        message="Are you sure you want to delete the card ?"
        iconClass="far fa-trash-alt"
        confirmButtonText="Delete"
        onConfirm={() => handleDeleteCard(deletingCardIndex)}
        disabled={isInLoadingState}
      />
    </Row>
  );
};

export default Cards;
